import { faUser } from '@fortawesome/pro-solid-svg-icons';
import { useFeatureGate } from '@statsig/react-bindings';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';

import { IconBadge, IconBadgeUse, Text, useRebrand } from '@growtherapy/sprout-ui';

import { AppointmentTime, type AppointmentTimeProps } from '../components/AppointmentTime';
import { useGetTelehealthSessionInfo } from '../hooks';
import { useVideoPlatform } from '../hooks/useVideoPlatform';
import { telehealthAdmitStatusIndicatorGate } from '../statsig/gates';
import { Toast, ToastRenderFnProps } from '../toasts';
import { ToastButton } from '../toasts/ToastButton';
import { VisitorPresence } from '../types';
import { noop } from '../utils';
import { VideoPlatform, normalizeVideoPlatform } from '../video-sdk';
import { JoiningClientLoader } from './admission/JoiningClientLoader';
import { type AdmittedClientStatusResult, useAdmittedClientStatus } from './admission/useAdmittedClientStatus';
import { useVerifyClientCompatibilityCallback } from './admission/useVerifyClientCompatibilityCallback';
import {
  inSessionPatientInformationAtom,
  joiningVisitorIdFamily,
  providerShortIdAtom,
  waitingVisitorsAtom,
} from './state';

export type ClientQueueToastProps = {
  admittedClientStatus?: AdmittedClientStatusResult;
  appointment?: AppointmentTimeProps['appointment'];
  buttonIsLoading?: boolean;
  currentSessionPatientShortId: VisitorPresence['patientShortId'] | null;
  granularStatusEnabled?: boolean;
  isAppointmentLoading?: AppointmentTimeProps['isLoading'];
  isIncompatible?: boolean;
  isZoom?: boolean;
  onAdmit: () => Promise<void>;
  onOpenQueue: () => void;
  /**
   * unlike onAdmit, this callback is required to avoid a funky edge-case when a
   * client with a different shortId joins the waiting room while the provider
   * is in a session with another client. if this becomes too cumbersome,
   * consider using onOpenQueue when the client is in a session with another
   * client
   */
  onShowStartNewSessionConfirmation: () => void;
  participantName: string;
  patientShortId: VisitorPresence['patientShortId'];
  visitorUuid?: string;
} & ToastRenderFnProps;

export function ClientQueueToast({
  admittedClientStatus,
  appointment,
  isAppointmentLoading,
  buttonIsLoading,
  currentSessionPatientShortId,
  granularStatusEnabled,
  isIncompatible,
  isZoom,
  onAdmit,
  onOpenQueue = noop,
  onShowStartNewSessionConfirmation,
  participantName,
  patientShortId,
  visitorUuid,
  contentProps,
  descriptionProps,
  titleProps,
  ...toastAriaProps
}: ClientQueueToastProps) {
  const isRebrand = useRebrand();
  const verbVariant = visitorUuid ? 'has' : 'have';
  const isRequestForNewSession =
    isIncompatible || (!!currentSessionPatientShortId && currentSessionPatientShortId !== patientShortId);

  const onButtonClick = async () => {
    // if a new session has been requested (e.g., patientShortId is not the
    // same as the current session patientShortId), show a confirmation
    if (isRequestForNewSession) {
      onShowStartNewSessionConfirmation();
      return;
    }

    if (visitorUuid) {
      await onAdmit();
      return;
    }

    onOpenQueue();
  };

  return (
    <Toast className='sm:w-[30rem]' data-testid='client-queue-toast' closeButtonSize='sm' {...toastAriaProps}>
      <button className='flex items-center flex-1 gap-3' onClick={onOpenQueue} aria-label='Open client queue'>
        <IconBadge
          aria-hidden
          icon={faUser}
          use={IconBadgeUse.Lilac}
          size='lg'
          className='arc:bg-surface-default'
          iconClassName='arc:text-text-default'
        />
        <div className='flex flex-col' {...contentProps}>
          <Text
            as='span'
            variant='sm'
            className='text-left fs-exclude'
            data-dd-privacy='mask'
            data-dd-action-name='Click on queue toast title'
            {...titleProps}
          >
            {participantName} {verbVariant} joined your waiting room
          </Text>
          <div className='flex flex-col' {...descriptionProps}>
            {isRequestForNewSession && (
              <AppointmentTime
                appointment={appointment}
                data-dd-action-name='appointment time'
                isLoading={isAppointmentLoading}
              />
            )}
          </div>
        </div>
      </button>
      {granularStatusEnabled && buttonIsLoading && admittedClientStatus ? (
        <JoiningClientLoader admittedClientStatus={admittedClientStatus} visitorName={participantName} />
      ) : (
        <ToastButton loading={buttonIsLoading} onClick={onButtonClick} use={isRebrand ? 'secondary' : 'primary'}>
          {visitorUuid ? (isZoom ? 'Invite in' : 'Admit') : 'View'}
        </ToastButton>
      )}
    </Toast>
  );
}

type WrapperProps = Omit<
  ClientQueueToastProps,
  | 'admittedClientStatus'
  | 'appointment'
  | 'isAppointmentLoading'
  | 'buttonIsLoading'
  | 'clientNameAnnotation'
  | 'currentSessionPatientShortId'
  | 'granularStatusEnabled'
  | 'providerCanSupportAllClients'
>;
export function ClientQueueToastWrapper(props: WrapperProps) {
  const { participantName, patientShortId, visitorUuid, closeToast } = props;
  const isWaitingForClientToJoin = useAtomValue(joiningVisitorIdFamily(visitorUuid ?? ''));
  const visitorPresenceMap = useAtomValue(waitingVisitorsAtom);
  const providerShortId = useAtomValue(providerShortIdAtom);
  const { patientShortId: currentSessionPatientShortId = null } = useAtomValue(inSessionPatientInformationAtom) ?? {};

  const { value: granularStatusEnabled } = useFeatureGate(telehealthAdmitStatusIndicatorGate);
  const admittedClientStatus = useAdmittedClientStatus({ visitorUuid: visitorUuid ?? '', granularStatusEnabled });

  const { data, loading } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const appointment = data?.telehealthSessionInfo?.appointment;
  const verifyClientCompatibility = useVerifyClientCompatibilityCallback();
  const isIncompatibleClient = !!visitorUuid && !verifyClientCompatibility(visitorUuid);
  const isZoom = normalizeVideoPlatform(useVideoPlatform()) === VideoPlatform.Zoom;

  useEffect(
    function dismissToastWhenVisitorLeaves() {
      // Dismiss the toast if the visitor has left the waiting room OR in the case of a multi-visitor generic toast, if all visitors have left
      if (
        (visitorUuid && !visitorPresenceMap[visitorUuid]) ||
        (!visitorUuid && !Object.keys(visitorPresenceMap).length)
      ) {
        closeToast();
      }
    },
    [closeToast, visitorPresenceMap, visitorUuid],
  );

  return (
    <ClientQueueToast
      {...props}
      admittedClientStatus={admittedClientStatus}
      buttonIsLoading={isWaitingForClientToJoin}
      currentSessionPatientShortId={currentSessionPatientShortId}
      granularStatusEnabled={granularStatusEnabled}
      isIncompatible={isIncompatibleClient}
      isZoom={isZoom}
      participantName={participantName}
      appointment={appointment}
      isAppointmentLoading={loading}
    />
  );
}
